import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useUIStore } from '../store/ui'
import { useNoteDrawerStore } from '../state/noteDrawer'
import { formatDate, todayStr } from '../lib/parse'

/**
 * Global keyboard shortcuts:
 * - Cmd/Ctrl+K         — open command palette
 * - Cmd/Ctrl+/         — focus today's todo input
 * - T (outside input)  — jump to today
 * - ←/→ (outside input) — prev/next window (by current day count)
 * - Cmd/Ctrl+D         — toggle dark mode
 */
export function useKeyboardShortcuts() {
  const openCommandPalette = useUIStore((s) => s.openCommandPalette)
  const commandPaletteOpen = useUIStore((s) => s.commandPaletteOpen)
  const closeCommandPalette = useUIStore((s) => s.closeCommandPalette)
  const focusedDate = useUIStore((s) => s.focusedDate)
  const setFocusedDate = useUIStore((s) => s.setFocusedDate)
  const weekDayCount = useUIStore((s) => s.weekDayCount)
  const themeMode = useUIStore((s) => s.themeMode)
  const setThemeMode = useUIStore((s) => s.setThemeMode)
  const noteDrawerOpen = useNoteDrawerStore((s) => s.todoId !== null)

  // Week-only shortcuts (T, ←, →) must not fire from /sections, /notes,
  // /settings — otherwise a user pressing arrow keys on those pages mutates
  // the weekly view's focusedDate behind their back.
  const { pathname } = useLocation()
  const onWeekPage = pathname === '/' || pathname.startsWith('/week')

  useEffect(() => {
    function handler(e: KeyboardEvent) {
      const isMac = navigator.platform.includes('Mac')
      const modifier = isMac ? e.metaKey : e.ctrlKey
      const activeEl = document.activeElement as HTMLElement | null
      const activeTag = activeEl?.tagName?.toLowerCase()
      // CodeMirror (the note editor) focuses a plain <div>, not an <input>, so a
      // tag-name check alone misses it. Also treat contenteditable hosts and any
      // element inside a CodeMirror instance (.cm-editor) as "in an input".
      const isInInput =
        !!activeTag &&
        (['input', 'textarea'].includes(activeTag) ||
          !!activeEl?.isContentEditable ||
          !!activeEl?.closest('.cm-editor'))

      // Cmd+K — toggle command palette
      if (modifier && e.key === 'k') {
        e.preventDefault()
        if (commandPaletteOpen) closeCommandPalette()
        else openCommandPalette()
        return
      }

      // Cmd+/ — focus today's input
      if (modifier && e.key === '/') {
        e.preventDefault()
        const today = todayStr()
        setFocusedDate(today)
        // Focus the todo input in the today column
        const todayInput = document.querySelector<HTMLInputElement>(`[data-date="${today}"] input`)
        todayInput?.focus()
        return
      }

      // Cmd+D — toggle dark mode
      if (modifier && e.key === 'd') {
        e.preventDefault()
        if (themeMode === 'dark') setThemeMode('light')
        else setThemeMode('dark')
        return
      }

      // Skip navigation shortcuts when in an input, while the note drawer is
      // open, or on a non-week page. The note-drawer guard is a belt-and-braces
      // backstop in case focus briefly leaves the editor while it's open.
      if (isInInput || commandPaletteOpen || noteDrawerOpen) return
      if (!onWeekPage) return

      // T — jump to today
      if (e.key === 't' || e.key === 'T') {
        setFocusedDate(todayStr())
        return
      }

      // ← — previous window (by current day count)
      if (e.key === 'ArrowLeft') {
        const d = new Date(focusedDate)
        d.setDate(d.getDate() - weekDayCount)
        setFocusedDate(formatDate(d))
        return
      }

      // → — next window (by current day count)
      if (e.key === 'ArrowRight') {
        const d = new Date(focusedDate)
        d.setDate(d.getDate() + weekDayCount)
        setFocusedDate(formatDate(d))
        return
      }
    }

    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [
    commandPaletteOpen,
    openCommandPalette,
    closeCommandPalette,
    focusedDate,
    setFocusedDate,
    weekDayCount,
    themeMode,
    setThemeMode,
    onWeekPage,
    noteDrawerOpen,
  ])
}
